import React, { useEffect, useRef, useState, useCallback } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { selectSidebarModal, useAppSelector } from '@open-tender/cloud'
import SidebarModalOverlay from './SidebarModalOverlay'

const SidebarModal = ({ children }: { children: React.ReactElement }) => {
  const sidebarRef = useRef<HTMLElement | null>(null)
  const [active, setActive] = useState<Element | null>(null)
  const [elements, setElements] = useState<Element[]>([])
  const { isOpen } = useAppSelector(selectSidebarModal)

  const handleExit = () => {
    if (active) (active as any).focus()
  }

  const handleFocus = () => {
    setActive(document.activeElement)
    const allElements = sidebarRef?.current?.querySelectorAll(
      'a[href], button, input, select, textarea'
    )
    let allElemtsArr = Array.from(allElements ?? [])
    setElements(allElemtsArr)
    const activeElements = Array.from(allElements ?? []).filter(
      (i: any) => !i.disabled
    )
    const lastElement = activeElements
      ? activeElements[activeElements.length - 1]
      : null
    if (lastElement) (lastElement as any).focus()
  }

  const handleKeys = useCallback(
    (evt: KeyboardEvent) => {
      if (evt.keyCode === 9 && sidebarRef.current && elements.length) {
        const activeElements = Array.from(elements).filter(
          (i: any) => !i.disabled
        )
        const firstElement: any = activeElements[0]
        const lastElement: any = activeElements[activeElements.length - 1]

        if (!evt.shiftKey && document.activeElement === lastElement) {
          firstElement.focus()
          evt.preventDefault()
        }

        if (evt.shiftKey && document.activeElement === firstElement) {
          lastElement.focus()
          evt.preventDefault()
        }
      }
    },
    [elements]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeys, false)
    return () => document.removeEventListener('keydown', handleKeys, false)
  }, [handleKeys])

  return (
    <>
      <SidebarModalOverlay />
      <TransitionGroup component={null}>
        {isOpen ? (
          <CSSTransition
            key="sidebar"
            classNames="sidebar"
            timeout={{ enter: 250, exit: 250 }}
            onEntered={handleFocus}
            onExited={handleExit}
          >
            {React.cloneElement(children, { ref: sidebarRef })}
          </CSSTransition>
        ) : null}
      </TransitionGroup>
    </>
  )
}

export default SidebarModal
